import MapView from '@arcgis/core/views/MapView';
import { MapViewInputTaskBreaker } from './MapViewInputTaskBreaker';

export type MapViewInputCursor = 'default' | 'crosshair' | 'pointer';

export abstract class MapViewInputTask<T> {
  protected readonly mapView: MapView;

  private __taskBreaker?: MapViewInputTaskBreaker;
  private __previousCursor?: string;
  private __resolvePromise?: (value: T) => void;
  private __rejectPromise?: (reason?: any) => void;
  private __eventHandle1?: IHandle;
  private __eventHandle2?: IHandle;
  private __eventHandle3?: IHandle;
  private __eventHandle4?: IHandle;

  constructor(mapView: MapView) {
    this.mapView = mapView;
    this.__taskBreaker = undefined;
  }

  public get isExecuting(): boolean {
    return this.__taskBreaker !== undefined;
  }

  public async executeTaskAsync(): Promise<T> {
    if (this.isExecuting) {
      await this.cancelTaskAsync();
    }

    this.__taskBreaker = new MapViewInputTaskBreaker();
    const surfaceElement: HTMLElement = (this.mapView as any).surface;
    this.__previousCursor = surfaceElement.dataset.cursor;
    try {
      const result = await this.__execute(this.__taskBreaker);
      return result;
    } finally {
      surfaceElement.dataset.cursor = this.__previousCursor;
      this.__taskBreaker = undefined;
      this.__rejectPromise = undefined;
      this.__resolvePromise = undefined;
    }
  }

  public async cancelTaskAsync(): Promise<void> {
    if (this.__taskBreaker) {
      await this.__taskBreaker.breakAsync();
    }
    this.__taskBreaker = undefined;
    this.__rejectPromise = undefined;
    this.__resolvePromise = undefined;
  }

  protected set cursor(value: MapViewInputCursor) {
    const surfaceElement: HTMLElement = (this.mapView as any).surface;
    surfaceElement.dataset.cursor = value;
  }

  protected completeNow(result: T) {
    if (this.__resolvePromise) {
      this.__cleanup();
      this.__resolvePromise(result);
    } else {
      throw new Error('Invalid operation. Resolver is undefined.');
    }
  }

  protected cancelNow() {
    if (this.__rejectPromise) {
      this.__cleanup();
      this.__rejectPromise(new Error('Input task cancelled.'));
    } else {
      throw new Error('Invalid operation. Resolver is undefined.');
    }
  }

  //overridable lifecycle methods
  protected onSetup(): void {
    //console.log('MapViewInputTask.onSetup()');
  }
  protected onCleanup(): void {
    // console.log('MapViewInputTask.onCleanup()');
  }
  protected onPointerMove(e: __esri.ViewPointerMoveEvent): void {
    // console.log('MapViewInputTask.onPointerMove()');
  }
  protected onImmediateClick(e: __esri.ViewImmediateClickEvent): void {
    // console.log('MapViewInputTask.onImmediateClick()');
  }
  protected onImmediateDoubleClick(e: __esri.ViewImmediateDoubleClickEvent): void {
    // console.log('MapViewInputTask.onImmediateDoubleClick()');
  }
  protected onKeyDown(e: __esri.ViewKeyDownEvent): void {
    // console.log('MapViewInputTask.onKeyDown()');
    if (e.key === 'Escape') {
      e.stopPropagation();
      this.cancelNow();
      return;
    }
  }
  private __execute(taskBreaker?: MapViewInputTaskBreaker): Promise<T> {
    // let resolvePromise: (value: T) => void;
    // let rejectPromise: (reason?: any) => void;
    const promise = new Promise<T>((resolve, reject) => {
      // resolvePromise = resolve;
      // rejectPromise = reject;
      this.__resolvePromise = resolve;
      this.__rejectPromise = reject;
    });

    const immediateClickHandler = (e: __esri.ViewImmediateClickEvent) => {
      this.onImmediateClick(e);
    };

    const immediateDoubleClickHandler = (e: __esri.ViewImmediateDoubleClickEvent) => {
      this.onImmediateDoubleClick(e);
    };

    const pointerMoveHandler = async (e: __esri.ViewPointerMoveEvent) => {
      this.onPointerMove(e);
    };

    const keyDownHandler = (e: __esri.ViewKeyDownEvent) => {
      this.onKeyDown(e);
    };

    // const cleanup = () => {
    //   eventHandle1.remove();
    //   eventHandle2.remove();
    //   eventHandle3.remove();
    //   eventHandle4.remove();
    //   this.onCleanup();
    // };

    if (taskBreaker) {
      taskBreaker.when(() => {
        // cleanup();
        // this.__cleanup();
        // if (this.__rejectPromise) {
        //   this.__rejectPromise(new Error('Action interrupted by user.'));
        // }
        this.cancelNow();
      });
    }

    // const eventHandle1 = this.mapView.on('key-down', keyDownHandler);
    // const eventHandle2 = this.mapView.on('pointer-move', pointerMoveHandler);
    // const eventHandle3 = this.mapView.on('immediate-click', immediateClickHandler);
    // const eventHandle4 = this.mapView.on('immediate-double-click', immediateDoubleClickHandler);
    this.__eventHandle1 = this.mapView.on('key-down', keyDownHandler);
    this.__eventHandle2 = this.mapView.on('pointer-move', pointerMoveHandler);
    this.__eventHandle3 = this.mapView.on('immediate-click', immediateClickHandler);
    this.__eventHandle4 = this.mapView.on('immediate-double-click', immediateDoubleClickHandler);

    //focus the mapView
    this.mapView.focus();
    this.onSetup();

    return promise;
  }

  private __cleanup() {
    if (this.__eventHandle1) this.__eventHandle1.remove();
    if (this.__eventHandle2) this.__eventHandle2.remove();
    if (this.__eventHandle3) this.__eventHandle3.remove();
    if (this.__eventHandle4) this.__eventHandle4.remove();

    this.__eventHandle1 = undefined;
    this.__eventHandle2 = undefined;
    this.__eventHandle3 = undefined;
    this.__eventHandle4 = undefined;

    this.onCleanup();
  }
}
